import React, { FC } from 'react';
import {
  Button,
  Col,
  Collapse,
  Divider,
  Empty,
  Progress,
  Row,
  Space,
  Typography,
  List,
} from 'antd';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getLimits } from '~/api/accounts';
import { AmountField } from '~/components';
import { usePath, useApi, useDeepEffect } from '~/hooks';
import { Account, Amount, Limit } from '~/types';
import constants from '~/utils/constants';
import { terminals } from '~/routes';
import EditAlias from './components/EditAlias';
import { getCurrentBalance } from '~/utils/amounts';
import { useAccountsPageQuery } from '~/hooks/accounts';
import { history } from '~/index';
import theme from '~/theme';

interface Props {
  className?: string;
}

const Accounts: FC<Props> = ({ className }) => {
  const [t] = useTranslation('accounts');
  const { pagination, changePath } = usePath();

  const { data: accounts, isLoading: loading, total, refetch } = useAccountsPageQuery();

  const pageSize = pagination.perPage;
  useDeepEffect(() => {
    const { pathname } = history.location;
    if (!pageSize && pathname !== '/accounts/balances') {
      changePath({ page: 1, perPage: pagination.defaultPageSize });
    }
  }, [pageSize]);

  return (
    <div className={className}>
      <List
        grid={{ gutter: 0, column: 1 }}
        dataSource={accounts}
        pagination={{
          ...pagination,
          total,
          showSizeChanger: true,
          pageSizeOptions: defaultPageSizeOptions,
        }}
        className="Accounts-List"
        renderItem={(item, index) => {
          const { alias, id, number } = item;
          const isFirst = index === 0;
          const isLast = index === accounts?.length - 1;
          return (
            <List.Item key={item.id}>
              <StyledCollapse isFirst={isFirst} isLast={isLast}>
                <Collapse.Panel
                  header={
                    <Typography.Text>
                      {`${alias || ''} ${number}`}
                      {item.status !== 'ACTIVE' ? ` (${t(`status:${item.status}`)})` : ''}
                    </Typography.Text>
                  }
                  key={id}
                  extra={<AmountField amount={getCurrentBalance(item as Account)?.amount} />}
                  collapsible={loading ? 'disabled' : undefined}
                >
                  {item && <DisplayLimits account={item as Account} reloadAccounts={refetch} />}
                </Collapse.Panel>
              </StyledCollapse>
            </List.Item>
          );
        }}
      />
    </div>
  );
};

interface DisplayLimitsProps {
  account: Account;
  reloadAccounts: () => void;
}

const DisplayLimits: FC<DisplayLimitsProps> = (props) => {
  const { account, reloadAccounts } = props;
  const [{ data, loading }] = useApi<Limit[]>(getLimits(account.id), [account]);
  const [t] = useTranslation('limits');
  const [tt] = useTranslation('terminals');
  const limitsWithProgress =
    data?.filter(
      (item) =>
        item.type === 'VELOCITY_CONTROL' &&
        item.velocityControl?.amount &&
        item.velocityControl?.window?.amount
    ) || [];
  const maxBalance = data?.find((item) => item.maxBalance);
  if (maxBalance) {
    limitsWithProgress.unshift(maxBalance);
  }
  const { changePath } = usePath();

  if (!loading && !data) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  return (
    <>
      <Space direction="vertical" size="large" className="Accounts-DisplayLimits">
        {limitsWithProgress?.map((limitItem) => {
          const amountFrom = limitItem.velocityControl?.window?.amount;
          const amountTo = limitItem.velocityControl?.amount;
          const limitName = limitItem.name ? t(limitItem.name) : '';
          if (limitItem.maxBalance) {
            return (
              <LimitRow
                key={limitItem.id}
                name={limitName}
                amountFrom={
                  account.balances.find((item) => item.balanceType === 'CURRENT_POSTED')?.amount
                }
                amountTo={limitItem.maxBalance}
              />
            );
          }
          return (
            <LimitRow
              key={limitItem.id}
              name={limitName}
              amountFrom={amountFrom}
              amountTo={amountTo}
            />
          );
        })}
      </Space>
      <Divider />
      <Space size={4}>
        <Button
          type="primary"
          ghost
          onClick={() => changePath({ route: `${terminals}`, filter: { accountId: account.id } })}
        >
          {tt('View Terminals')}
        </Button>
        <EditAlias account={account} reloadAccounts={reloadAccounts} />
      </Space>
    </>
  );
};

interface LimitRowProps {
  amountFrom: Amount | undefined;
  amountTo: Amount | undefined;
  name: string | undefined;
}

const LimitRow: FC<LimitRowProps> = (props) => {
  const { name, amountFrom, amountTo } = props;
  const percent = ((amountFrom?.value || 0) / (amountTo?.value || 1)) * 100;

  return (
    <Row gutter={[16, 24]}>
      <Col flex="250px">
        <Typography.Text>{name || 'Unnamed limit'}</Typography.Text>
      </Col>
      <Col flex="auto">
        <Row align="middle" gutter={8}>
          <Col flex="100px" className="Accounts-AmountFrom">
            <AmountField className="text-dark-gray" amount={amountFrom} />
          </Col>
          <Col flex="auto">
            <Progress
              strokeColor={{
                '0%': constants.primaryColor,
                '100%': constants.primaryColor,
              }}
              percent={percent}
              showInfo={false}
            />
          </Col>
          <Col flex="100px">
            <AmountField className="text-dark-gray" amount={amountTo} />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

const defaultPageSizeOptions = [10, 25, 50];

interface StyledCollapseProps {
  isFirst?: boolean;
  isLast?: boolean;
}

const StyledCollapse = styled<FC<StyledCollapseProps>>(Collapse as any)`
  border-radius: 0;
  border-top-left-radius: ${({ isFirst }) => (isFirst ? `${theme.borderRadiusBase}px` : undefined)};
  border-top-right-radius: ${({ isFirst }) =>
    isFirst ? `${theme.borderRadiusBase}px` : undefined};
  border-bottom-left-radius: ${({ isLast }) =>
    isLast ? `${theme.borderRadiusBase}px` : undefined};
  border-bottom-right-radius: ${({ isLast }) =>
    isLast ? `${theme.borderRadiusBase}px` : undefined};

  border-bottom: ${({ isLast }) => (isLast ? '1px solid #e6e6e6' : undefined)};

  &.ant-collapse {
    .ant-collapse-item {
      border-bottom: 0;
    }
  }
`;

export default styled(Accounts)`
  .Accounts-DisplayLimits {
    width: 100%;
    .Accounts-AmountFrom {
      display: flex;
      justify-content: flex-end;
    }
  }

  .Accounts-List.ant-list.ant-list-grid {
    .ant-col > .ant-list-item.ant-list-item {
      margin-bottom: 0;
    }
  }
`;
